import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Stack, EnvelopeSimple, Trash, ArrowSquareOut, CircleNotch, ShieldCheck, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const fmt = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d) ? String(s) : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) + " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const TABS = [
  { k: "challenges", label: "Challenges", icon: Stack },
  { k: "users", label: "Users", icon: Users },
  { k: "emails", label: "Sent emails", icon: EnvelopeSimple },
  { k: "feedback", label: "Feedback", icon: ChatCircleDots },
];

const Stat = ({ label, value }) => (
  <div className="border border-border bg-card px-5 py-3">
    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-1 font-serif text-3xl">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("challenges");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [emails, setEmails] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, c, e, f] = await Promise.all([api.adminStats(), api.adminUsers(), api.adminChallenges(), api.adminEmails(), api.adminFeedback()]);
      setStats(s); setUsers(u); setChallenges(c); setEmails(e); setFeedback(f);
    } catch (err) {
      toast.error("Could not load admin data", { description: err?.response?.data?.detail || err.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const delUser = async (u) => {
    if (!window.confirm(`Delete ${u.email} and ALL their challenges? This cannot be undone.`)) return;
    try { const r = await api.adminDeleteUser(u.id); toast.success(`Deleted ${u.email}`, { description: `${r.sessions_removed} challenge(s) removed` }); load(); }
    catch (err) { toast.error("Delete failed", { description: err?.response?.data?.detail || err.message }); }
  };
  const delChallenge = async (c) => {
    if (!window.confirm(`Delete this challenge by ${c.user_email}? This cannot be undone.`)) return;
    try { await api.adminDeleteChallenge(c.id); toast.success("Challenge deleted"); load(); }
    catch (err) { toast.error("Delete failed", { description: err?.response?.data?.detail || err.message }); }
  };

  return (
    <div data-testid="admin-dashboard" className="mx-auto w-full max-w-[1200px] px-8 py-10">
      <div className="flex items-center gap-2">
        <ShieldCheck size={22} weight="fill" className="text-[#0F62FE]" />
        <h1 className="font-serif text-3xl">Admin</h1>
      </div>
      <p className="mt-1.5 text-sm text-muted-foreground">Signed in as <strong>{user?.email}</strong> — you can see every user, the challenges they've entered, and the emails the app has sent.</p>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Users" value={stats?.users ?? "—"} />
        <Stat label="Challenges" value={stats?.challenges ?? "—"} />
        <Stat label="Emails sent" value={stats?.emails_sent ?? "—"} />
        <Stat label="Feedback" value={stats?.feedback ?? "—"} />
      </div>

      <div className="mt-8 flex gap-1 border-b border-border">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === t.k ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <Icon size={15} /> {t.label}
              <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px]">{t.k === "users" ? users.length : t.k === "challenges" ? challenges.length : t.k === "feedback" ? feedback.length : emails.length}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="grid place-items-center py-24"><CircleNotch size={24} className="animate-spin text-foreground/60" /></div>
      ) : (
        <div className="mt-5">
          {tab === "challenges" && (
            <div data-testid="admin-challenges" className="overflow-x-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr><th className="px-4 py-2.5">User</th><th className="px-4 py-2.5">Challenge</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Entered</th><th className="px-4 py-2.5 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {challenges.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No challenges yet.</td></tr>}
                  {challenges.map((c) => (
                    <tr key={c.id} data-testid={`admin-challenge-row-${c.id}`} className="border-t border-border align-top hover:bg-muted/30">
                      <td className="px-4 py-3 whitespace-nowrap text-foreground/90">{c.user_email}</td>
                      <td className="px-4 py-3"><div className="font-medium">{c.title}</div>{c.narrative && <div className="mt-0.5 max-w-[520px] text-xs leading-snug text-muted-foreground line-clamp-2">{c.narrative}</div>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">{c.status}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">{fmt(c.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button data-testid={`admin-open-challenge-${c.id}`} onClick={() => nav(`/sessions/${c.id}`)} className="inline-flex items-center gap-1 rounded-sm border border-border px-2.5 py-1 text-xs hover:bg-muted"><ArrowSquareOut size={13} /> Open</button>
                          <button data-testid={`admin-delete-challenge-${c.id}`} onClick={() => delChallenge(c)} className="inline-flex items-center gap-1 rounded-sm border border-destructive/40 px-2.5 py-1 text-xs text-destructive hover:bg-destructive/5"><Trash size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "users" && (
            <div data-testid="admin-users" className="overflow-x-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Challenges</th><th className="px-4 py-2.5">Joined</th><th className="px-4 py-2.5 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{u.email}</td>
                      <td className="px-4 py-3 text-muted-foreground">{u.name || "—"}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${u.role === "admin" ? "bg-[#0F62FE]/10 text-[#0F62FE]" : "bg-muted text-muted-foreground"}`}>{u.role || "user"}</span></td>
                      <td className="px-4 py-3 text-muted-foreground">{u.session_count}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">{fmt(u.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button data-testid={`admin-delete-user-${u.id}`} disabled={u.id === user?.id || u.role === "admin"} onClick={() => delUser(u)} className="inline-flex items-center gap-1 rounded-sm border border-destructive/40 px-2.5 py-1 text-xs text-destructive hover:bg-destructive/5 disabled:cursor-not-allowed disabled:opacity-30"><Trash size={13} /> Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "emails" && (
            <div data-testid="admin-emails" className="overflow-x-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">To</th><th className="px-4 py-2.5">Subject</th><th className="px-4 py-2.5">Sent by</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">When</th></tr>
                </thead>
                <tbody>
                  {emails.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No emails have been sent yet. Password-reset and report emails will appear here.</td></tr>}
                  {emails.map((e) => (
                    <tr key={e.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3 whitespace-nowrap capitalize text-muted-foreground">{(e.kind || "").replace("_", " ")}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium">{e.to}</td>
                      <td className="px-4 py-3 max-w-[320px] truncate">{e.subject}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">{e.sent_by || "—"}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-medium ${e.status === "sent" ? "text-[#24A148]" : "text-destructive"}`}>{e.status}</span></td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">{fmt(e.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "feedback" && (
            <div data-testid="admin-feedback" className="overflow-x-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr><th className="px-4 py-2.5">Feedback</th><th className="px-4 py-2.5">From</th><th className="px-4 py-2.5">Page</th><th className="px-4 py-2.5">When</th></tr>
                </thead>
                <tbody>
                  {feedback.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No feedback yet. Submissions from the feedback bar/card will appear here.</td></tr>}
                  {feedback.map((f) => (
                    <tr key={f.id} className="border-t border-border align-top hover:bg-muted/30">
                      <td className="px-4 py-3 max-w-[560px] whitespace-pre-wrap leading-snug">{f.message}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">{f.email || "anonymous"}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-[11px] text-muted-foreground">{f.page || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">{fmt(f.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
